import PropTypes from 'prop-types';
import { useRef, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import html2canvas from 'html2canvas';
import { Download } from 'lucide-react';
// custom
import { ROUTES } from '@app/router/routes';
import { usePageLayout } from '@app/router';
import { getVowRecordDescribe } from '../api/lampService';
import { DEFAULT_BLESSING_DATA, DEFAULT_LAMP_INFO } from '../constants';
import './BlessingResultPage.css';
import { useLanguage } from '@shared/contexts/LanguageContext';

const RESULT_BG_IMAGES = [
    '/image/wishlamp/image_250_x2.png',
    '/image/wishlamp/image_252_x2.png',
];

/**
 * 解析 API 回傳的 content 字串
 * 格式: "法語內容<br><br>－－作者名"
 * 回傳: { text, author }
 */
/** 依內文字數縮小法語內文（inline，行動與桌面視窗皆一致套用） */
const getBlessingBodyFontSizeStyle = (text) => {
    const len = text?.length ?? 0;
    // fontSize 與 lineHeight 一起縮，避免行距過大浪費空間
    if (len > 380) return { fontSize: '12px', lineHeight: '18px' };
    if (len > 340) return { fontSize: '13px', lineHeight: '19px' };
    if (len > 300) return { fontSize: '14px', lineHeight: '21px' };
    if (len > 260) return { fontSize: '15px', lineHeight: '23px' };
    if (len > 220) return { fontSize: '16px', lineHeight: '25px' };
    if (len > 190) return { fontSize: '17px', lineHeight: '27px' };
    if (len > 180) return { fontSize: '15px', lineHeight: '24px' };
    if (len > 170) return { fontSize: '16px', lineHeight: '26px' };
    if (len > 160) return { fontSize: '16px', lineHeight: '26px' };
    if (len > 150) return { fontSize: '17px', lineHeight: '28px' };
    if (len > 140) return { fontSize: '17px', lineHeight: '28px' };
    if (len > 130) return { fontSize: '17px', lineHeight: '28px' };
    if (len > 120) return { fontSize: '17px', lineHeight: '28px' };
    if (len > 110) return { fontSize: '17px', lineHeight: '28px' };
    if (len > 100) return { fontSize: '18px', lineHeight: '28px' };
    if (len > 90) return { fontSize: '19px', lineHeight: '28px' };

    return undefined;
};

const parseContent = (raw) => {
    if (!raw) return { text: '', author: '' };
    // 用 <br><br>－－ 或 <br><br>—— 分隔內容與作者
    const match = raw.match(/^([\s\S]*?)<br\s*\/?><br\s*\/?>[-－—]+\s*(.*)$/);
    if (match) {
        return {
            text: match[1].replace(/<br\s*\/?>/g, '\n'),
            author: match[2],
        };
    }
    return { text: raw.replace(/<br\s*\/?>/g, '\n'), author: '' };
};

const BlessingResultPage = ({
    blessingData: propBlessingData,
    lampInfo: propLampInfo,
    onComplete,
    onShare,
}) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { setLayoutOptions } = usePageLayout();
    const { t, currentLanguage } = useLanguage();
    const isEn = currentLanguage.code === 'en';
    const frameRef = useRef(null);

    useEffect(() => {
        setLayoutOptions({ hideHeader: true, hideFooter: true, hideBottomNav: true });
        return () => setLayoutOptions({});
    }, [setLayoutOptions]);
    const [downloading, setDownloading] = useState(false);
    const [bgImage] = useState(() => RESULT_BG_IMAGES[Math.floor(Math.random() * RESULT_BG_IMAGES.length)]);
    const isBg252 = bgImage.includes('image_252');

    const [fetchedResult, setFetchedResult] = useState(null);
    const [isLoading, setIsLoading] = useState(!!location.state?.recordId);

    const recordId = location.state?.recordId;
    // 優先使用 route state 中 API 回傳的資料，否則 fallback 到 props / 預設值
    const apiResult = fetchedResult || location.state?.blessingResult;

    useEffect(() => {
        if (recordId) {
            setIsLoading(true);
            getVowRecordDescribe(recordId)
                .then(res => {
                    setFetchedResult(res);
                })
                .catch(err => {
                    console.error('Failed to fetch vow record:', err);
                })
                .finally(() => {
                    setIsLoading(false);
                });
        }
    }, [recordId]);

    // 若沒有 state（直接進入此頁），redirect 回傳燈首頁
    useEffect(() => {
        if (!isLoading && !recordId && !apiResult && !propBlessingData) {
            navigate(ROUTES.WISDOM_LAMP, { replace: true });
        }
    }, [isLoading, recordId, apiResult, propBlessingData, navigate]);

    if (isLoading || (!apiResult && !propBlessingData)) return null;

    let blessingData, lampInfo;
    if (apiResult) {
        const parsed = parseContent(apiResult.content);
        blessingData = {
            label: t('blessing-result.label'),
            title: apiResult.title,
            content: parsed.text,
            author: parsed.author,
        };
        lampInfo = {
            branch: apiResult.monastery_name || '',
            donor: apiResult.vower_name || '',
        };
    } else {
        blessingData = propBlessingData || DEFAULT_BLESSING_DATA;
        lampInfo = propLampInfo || DEFAULT_LAMP_INFO;
    }

    const blessingBodyFontStyle = getBlessingBodyFontSizeStyle(blessingData.content);

    const handleComplete = () => {
        if (onComplete) {
            onComplete();
        } else {
            navigate(ROUTES.LAMP_BULLETIN);
        }
    };

    const handleShareDownload = async () => {
        if (!frameRef.current || downloading) return;
        setDownloading(true);
        try {
            const canvas = await html2canvas(frameRef.current, {
                scale: 2,
                useCORS: true,
                backgroundColor: null,
                ignoreElements: (el) => !!el.closest?.('.blessing-result-actions'),
            });
            const safeTitle = (blessingData.title || '分享')
                .replace(/[/\\?%*:|"<>]/g, '_')
                .slice(0, 40);
            const link = document.createElement('a');
            link.download = `祈福法語_${safeTitle}.png`;
            link.href = canvas.toDataURL('image/png');
            link.click();
            onShare?.();
        } finally {
            setDownloading(false);
        }
    };

    //------------------------------------------------------------------------------
    return (
        <div className="blessing-result-page">
            {/* 關閉按鈕 */}
            <button
                onClick={() => navigate(location.state?.from ?? -2)}
                className="fixed top-4 right-4 z-50 w-10 h-10 flex items-center justify-center rounded-full bg-black/40 hover:bg-black/60 transition-colors"
                aria-label={t('blessing-result.closeAria')}
            >
                <span className="material-symbols-outlined text-2xl text-white">close</span>
            </button>

            <section
                className={`blessing-result-frame${isBg252 ? ' blessing-result-frame--252' : ''}`}
                aria-label={t('blessing-result.resultAria')}
                data-layer="文星點燈_註冊"
            >
                <div>
                    <div ref={frameRef} className="blessing-result-body">
                        <div
                            className="blessing-result-deco"
                            data-layer={isBg252 ? 'image 252' : 'image 250'}
                            aria-hidden
                        >
                            <img
                                className="blessing-result-deco-img"
                                src={bgImage}
                                alt=""
                            />
                        </div>

                        <div className="blessing-result-body-inner">
                            <div className="blessing-result-label-block">
                                {!isEn && <hr className="blessing-result-line" data-layer="Line 531" />}
                                <h2 className="blessing-result-label" data-layer="法 語 開 示" style={isEn ? { maxWidth: '180px', height: 'auto', minHeight: '40px', lineHeight: '28px', wordBreak: 'break-word' } : undefined}>
                                    {blessingData.label}
                                </h2>
                                {!isEn && <hr className="blessing-result-line" data-layer="Line 530" />}
                            </div>
                            <h1 className="blessing-result-title" data-layer="家庭和樂" style={isEn ? { fontSize: '22px', lineHeight: '30px', maxWidth: '220px' } : undefined}>
                                {blessingData.title}
                            </h1>

                            <p
                                className="blessing-result-content"
                                style={blessingBodyFontStyle}
                                data-layer="中國有一句名言"
                            >
                                {blessingData.content}
                            </p>
                            {blessingData.author ? (
                                <p className="blessing-result-author">──{blessingData.author}</p>
                            ) : null}

                            <p
                                className="blessing-result-lamp-info"
                                data-layer="普天精舍　王大明　點供"
                            >
                                {isEn
                                    ? `${t('blessing-result.offering')} ${lampInfo.donor}`
                                    : [lampInfo.branch, lampInfo.donor, t('blessing-result.offering')]
                                        .filter(Boolean)
                                        .join('　')}
                            </p>
                        </div>
                    </div>

                    <div className="blessing-result-actions">
                        <button
                            type="button"
                            className="blessing-result-btn blessing-result-btn--complete"
                            data-layer="Rectangle 4367"
                            onClick={handleComplete}
                        >
                            <span data-layer="點燈完成">{t('blessing-result.completeBtn')}</span>
                        </button>
                        <button
                            type="button"
                            className="blessing-result-btn blessing-result-btn--share"
                            data-layer="Rectangle 4366"
                            disabled={downloading}
                            onClick={handleShareDownload}
                        >
                            <span data-layer="下載" style={downloading && isEn ? { fontSize: '13px', lineHeight: '18px' } : undefined}>
                                {downloading ? t('blessing-result.generatingImage') : t('blessing-result.downloadBtn')}
                            </span>
                            {!downloading &&
                                <Download size={24} color="#e20020" strokeWidth={2} aria-hidden />}
                        </button>
                    </div>
                </div>
            </section>
        </div>
    );
};

BlessingResultPage.propTypes = {
    blessingData: PropTypes.shape({
        label: PropTypes.string,
        title: PropTypes.string,
        content: PropTypes.string,
        author: PropTypes.string,
    }),
    lampInfo: PropTypes.shape({
        branch: PropTypes.string,
        donor: PropTypes.string,
    }),
    onComplete: PropTypes.func,
    onShare: PropTypes.func,
};

export default BlessingResultPage;
